/**
 * 토폴로지 뷰 노드 (디자인: 전사_토폴로지 뷰 PNG).
 * - 설비(source): 지름 40 원. 테두리 = 상태색. 경고·오류는 안에 "!", 복구 중은 회전 화살표, 정지는 "×".
 *   아래에 "07호기" 처럼 짧은 이름 라벨.
 * - 처리기·저장소·출력: 60 둥근 사각. 가운데 약어(수집·OB·UP·DB·출력), 아래 두 줄 라벨("OPC UA 수집기 / (베트남)").
 * 엣지는 도형 중심끼리 직선으로 잇는다 → 핸들을 도형 중심에 숨겨 둔다.
 * 상태는 색 + 기호 + aria-label 로 함께 나타낸다(색만으로 구분하지 않음).
 */
import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { usePipelineStore } from "../../store/pipeline-store";
import { STATUS_LABEL_KO, formatThroughput } from "../../lib/format";
import { nodeKind } from "../../lib/node-ids";
import { BOX_CENTER, MACHINE_CENTER } from "../../lib/layout-pipeline";

/** 상태어: 설비는 "수집 중", 처리기·저장소는 "처리 중" */
export function statusLabel(status, nodeType) {
    if (status === "running" && nodeType !== "source")
        return "처리 중";
    return STATUS_LABEL_KO[status];
}

/** "베트남 07호기" → "07호기" (공장명 접두어 제거) */
export function shortMachineLabel(label) {
    const m = /(\d+\s*호기)$/.exec(label || "");
    return m ? m[1].replace(/\s+/g, "") : label;
}

/** 사각 노드 가운데 약어 */
function boxCode(id) {
    switch (nodeKind(id)) {
        case "collector": return "수집";
        case "outbox": return "OB";
        case "uploader": return "UP";
        case "table":
        case "legacy": return "DB";
        case "output": return "출력";
        default: return "";
    }
}

/** 사각 노드 라벨 두 줄: "OPC UA 수집기 (베트남)" → ["OPC UA 수집기", "(베트남)"] */
function boxLabelLines(id, label) {
    const text = nodeKind(id) === "outbox" ? label.replace(/^Outbox/i, "OUTBOX") : label;
    const m = /^(.*?)\s*(\([^)]*\))$/.exec(text);
    return m ? [m[1], m[2]] : [text];
}

const MACHINE_MARK = { warning: "!", error: "!", offline: "×" };

const hiddenHandle = (center) => ({ left: center.x, top: center.y, width: 1, height: 1, minWidth: 0, minHeight: 0, opacity: 0, border: 0, transform: "translate(-50%, -50%)" });
const MACHINE_HANDLE = hiddenHandle(MACHINE_CENTER);
const BOX_HANDLE = hiddenHandle(BOX_CENTER);

function onKey(id) {
    return (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            usePipelineStore.getState().actions.selectNode(id);
        }
        else if (e.key === "Escape") {
            e.stopPropagation();
            usePipelineStore.getState().actions.selectNode(null);
        }
    };
}

function PipelineNodeComponent({ id, data, selected }) {
    const label = statusLabel(data.status, data.nodeType);
    const aria = `${data.label}, ${label}, 처리량 ${formatThroughput(data.throughput, data.nodeType)}`;
    const isMachine = nodeKind(id) === "machine";
    const handleStyle = isMachine ? MACHINE_HANDLE : BOX_HANDLE;

    // 마우스 클릭·더블클릭은 PipelineCanvas 의 onNodeClick/onNodeDoubleClick 이 처리한다(중복 선택 방지)
    return (<div className={`dfv-node ${isMachine ? "dfv-node--machine" : "dfv-node--box"}${selected ? " is-selected" : ""}`} data-status={data.status} role="button" tabIndex={0} aria-pressed={!!selected} aria-label={aria} title={data.description} onKeyDown={onKey(id)}>
      <Handle type="target" position={Position.Left} isConnectable={false} style={handleStyle} aria-hidden="true"/>
      {isMachine ? (<>
          <span className="dfv-node__circle" aria-hidden="true">
            {data.status === "recovering"
            ? <span className="material-symbols-outlined dfv-node__spin">autorenew</span>
            : MACHINE_MARK[data.status] && <span className="dfv-node__mark">{MACHINE_MARK[data.status]}</span>}
          </span>
          <span className="dfv-node__tag">{shortMachineLabel(data.label)}</span>
        </>) : (<>
          <span className="dfv-node__box" aria-hidden="true">
            <i className="dfv-node__nub dfv-node__nub--t"/>
            <i className="dfv-node__nub dfv-node__nub--b"/>
            <i className="dfv-node__nub dfv-node__nub--l"/>
            <i className="dfv-node__nub dfv-node__nub--r"/>
            {boxCode(id)}
          </span>
          <span className="dfv-node__caption">
            {boxLabelLines(id, data.label).map((line) => <span key={line}>{line}</span>)}
          </span>
        </>)}
      <Handle type="source" position={Position.Right} isConnectable={false} style={handleStyle} aria-hidden="true"/>
    </div>);
}
export const PipelineNode = memo(PipelineNodeComponent);
PipelineNode.displayName = "PipelineNode";
export default PipelineNode;
